/**
 * Document throughput and ST-to-D/T status panels.
 */
import { formatInteger, formatPercent } from "./format.js";
import {
  barWidth,
  escapeAttr,
  escapeHtml,
  labelize,
  pill
} from "./dashboard-html.js";
import type {
  LeadFactoryDocumentThroughput,
  LeadFactoryDocumentThroughputStage,
  LeadFactoryStToDtStatus
} from "./dashboard-lead-factory-types.js";

export function documentThroughputHtml(throughput: LeadFactoryDocumentThroughput): string {
  return `
    <section class="document-throughput-panel" data-document-throughput-panel>
      <div class="panel-heading">
        <h2>Document Throughput</h2>
        <span class="subtle">${formatInteger(throughput.documents.total)} source docs · ${formatInteger(throughput.tasks.active)} active document tasks</span>
      </div>
      <div class="document-throughput-grid">
        ${documentThroughputStageHtml(throughput.stages.find((stage) => stage.key === "classification"))}
        ${documentThroughputStageHtml(throughput.stages.find((stage) => stage.key === "extraction"))}
        ${documentThroughputStageHtml(throughput.stages.find((stage) => stage.key === "ocr"))}
        ${documentThroughputStageHtml(throughput.stages.find((stage) => stage.key === "review"))}
      </div>
      <div class="document-throughput-footer">
        <span data-document-throughput-classification>${formatPercent(throughput.documents.classificationRate)} classified</span>
        <span data-document-throughput-extraction>${formatPercent(throughput.documents.extractionCompletionRate)} extracted</span>
        <span data-document-throughput-ocr>${formatPercent(throughput.documents.ocrCoverageRate)} OCR evidence</span>
        <span>${formatInteger(throughput.tasks.overdue)} overdue</span>
      </div>
    </section>
  `;
}

export function stToDtStatusHtml(status: LeadFactoryStToDtStatus): string {
  const summary = status.summary;
  const scheduler = status.scheduler ?? {
    status: "unknown",
    source: "not_configured",
    currentBlocker: null,
    message: "Scheduler health has not been published to the dashboard status endpoint.",
    lastRunId: null,
    lastRunUrl: null,
    lastRunAt: null
  };
  const schedulerDetail = scheduler.currentBlocker || scheduler.message || "No scheduler blocker reported.";
  return `
    <section class="st-to-dt-status-panel" data-st-to-dt-status>
      <div class="panel-heading">
        <h2>ST to D/T Automation</h2>
        <span class="subtle">${formatInteger(summary.automationBacklog)} active work signals · ${formatInteger(summary.totalStDocuments)} ST/source docs</span>
      </div>
      <div class="st-to-dt-scheduler" data-st-to-dt-scheduler-status="${escapeAttr(scheduler.status)}">
        <strong>${escapeHtml(labelize(scheduler.status))}</strong>
        <span data-st-to-dt-scheduler-message>${escapeHtml(schedulerDetail)}</span>
        ${
          scheduler.lastRunUrl
            ? `<a href="${escapeAttr(scheduler.lastRunUrl)}" target="_blank" rel="noreferrer" data-st-to-dt-scheduler-run>Latest run</a>`
            : `<span data-st-to-dt-scheduler-run>${escapeHtml(scheduler.lastRunId ? `run ${scheduler.lastRunId}` : "no run link")}</span>`
        }
      </div>
      <div class="st-to-dt-status-grid">
        ${stToDtStatusMetricHtml("Backlog", summary.automationBacklog, "automation work signals", "backlog")}
        ${stToDtStatusMetricHtml("Pending OCR", summary.pendingOcr, "OCR/text prerequisites", "pending-ocr")}
        ${stToDtStatusMetricHtml("Refs Recovered", summary.referencesRecovered, "book/page found", "references-recovered")}
        ${stToDtStatusMetricHtml("Retrieval Queue", summary.retrievalJobsQueued + summary.retrievalJobsClaimed, `${formatInteger(summary.retrievalJobsCompleted)} completed`, "retrieval-queue")}
        ${stToDtStatusMetricHtml("D/T Complete", summary.factCompleteDeedsOfTrust, `${formatInteger(summary.totalDeedsOfTrust)} D/T docs`, "fact-complete")}
        ${stToDtStatusMetricHtml("Manual/Terminal", summary.terminalOrManualEvidence, "typed reasons", "terminal")}
      </div>
      <div class="st-to-dt-status-footer">
        <span data-st-to-dt-backlog>${formatInteger(summary.automationBacklog)} backlog</span>
        <span data-st-to-dt-pending-ocr>${formatInteger(summary.pendingOcr)} OCR</span>
        <span data-st-to-dt-references-recovered>${formatInteger(summary.referencesRecovered)} references</span>
        <span data-st-to-dt-jobs-queued>${formatInteger(summary.retrievalJobsQueued)} queued</span>
        <span data-st-to-dt-runner-completed>${formatInteger(summary.retrievalJobsCompleted)} completed</span>
        <span data-st-to-dt-runner-failed>${formatInteger(summary.retrievalJobsFailed)} failed</span>
        <span data-st-to-dt-fact-complete>${formatInteger(summary.factCompleteDeedsOfTrust)} D/T complete</span>
        <span data-st-to-dt-terminal>${formatInteger(summary.terminalOrManualEvidence)} terminal/manual</span>
      </div>
      <div class="st-to-dt-terminal-reasons" data-st-to-dt-terminal-reasons>
        ${Object.entries(status.terminalReasons)
          .slice(0, 5)
          .map(([reason, total]) => `<span>${escapeHtml(labelize(reason))}: ${formatInteger(total)}</span>`)
          .join("") || `<span>No terminal/manual reasons visible.</span>`}
      </div>
    </section>
  `;
}

export function stToDtStatusMetricHtml(label: string, value: number, detail: string, key: string): string {
  return `
    <article class="st-to-dt-status-metric" data-st-to-dt-status-metric="${escapeAttr(key)}">
      <span>${escapeHtml(label)}</span>
      <strong>${formatInteger(value)}</strong>
      <small>${escapeHtml(detail)}</small>
    </article>
  `;
}

export function documentThroughputStageHtml(stage: LeadFactoryDocumentThroughputStage | undefined): string {
  if (!stage) return "";
  return `
    <article class="document-throughput-stage" data-document-throughput-stage="${escapeAttr(stage.key)}">
      <div>
        <strong>${escapeHtml(stage.label)}</strong>
        <small>${formatInteger(stage.completed)} / ${formatInteger(stage.total)} complete</small>
      </div>
      ${pill(`${formatInteger(stage.backlog)} backlog`, stage.severity)}
      <div class="throughput-bar" aria-hidden="true">
        <span style="width: ${escapeAttr(barWidth(stage.completionRate))}%"></span>
      </div>
      <p>${escapeHtml(stage.nextAction)}</p>
    </article>
  `;
}

